import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InkContactMenu from 'components/InkContactMenu';
import 'styles/ink-site.css';

export const LOGO_64 = '/assets/images/ink/logo-64.webp';
export const LOGO_256 = '/assets/images/ink/logo-256.webp';

/* Installers live on the public GitHub releases repo (Vercel caps files at ~100 MB).
   Release v0.1.21: Windows and Linux installers from frozen source f364b181.
   Change the version/build here only; every card reads from this array.
   On each release: update RELEASE, REL_BASE and RELEASES, and add the new build to the
   TOP of CHANGELOG (user-facing wording; only claim what the build's provenance verifies). */
export const RELEASE = { version: 'v0.1.21', build: 'f364b181', date: '3 Nov 2026' };
const REL_BASE = 'https://github.com/lee-m-dmp/Neevii-Ink-Downloads/releases/download/v0.1.21';
export const RELEASES = [
  {
    key: 'linux',
    platform: 'Linux',
    tag: 'Recommended for studios',
    arch: 'Ubuntu 24.04+ · x86_64 · glibc 2.39',
    size: '172 MB',
    format: 'offline .run installer',
    url: `${REL_BASE}/Neevii-Ink-0.1.21-f364b181-linux-x86_64.run`,
    hint: 'chmod +x the .run, then run it. Installs per-user, no root or FUSE needed.',
  },
  {
    key: 'win',
    platform: 'Windows',
    tag: 'Also native',
    arch: 'Windows 10/11 · 64-bit',
    size: '318 MB',
    format: 'offline installer (.exe)',
    url: `${REL_BASE}/Neevii.Ink_0.1.21_x64-setup.exe`,
    hint: 'WebView2 runtime included, so setup needs no internet. Unsigned build: Windows may show a publisher prompt.',
  },
];
export const CHECKSUMS_URL = `${REL_BASE}/SHA256SUMS.txt`;

/* Release notes, newest first. The first entry is shown open as the latest build. */
export const CHANGELOG = [
  { version: 'v0.1.21', build: 'f364b181', date: '3 Nov 2026', added: [
    'Copy and paste editable layer groups between documents with Ctrl+C and Ctrl+V.',
    'Keep an independent document copy with File → Save Backup.'
  ], fixes: [
    'Copy newly created and duplicated groups without needing to save first, and drop groups below the bottom of the layer stack without nesting them.',
    'Flatten affected half-float documents reliably, including documents with editable text.',
    'Keep the surviving document canvas visible when closing another document.',
    'Reduce recovery-file growth with two rotating copies per document and autosave only when edits have changed.',
    'Limit inactive decoded-image cache generations while protecting open documents and data needed for recovery.'
  ]},
  { version: 'v0.1.20', build: '6b06e5b3', date: '3 Nov 2026', added: [
    'Toggle layer clipping directly from the layer row, with clearer clipped-layer indentation.',
    'Use pressure shortcuts in the Brush, Eraser and Blend headers, and a moving source crosshair for Clone Stamp.',
    'Choose Point Sample or larger average areas with the Eyedropper.',
    'Preview installed fonts live in a movable browser that stays open beside the text controls.'
  ], fixes: [
    'Improve layer merge speed and preserve clipped content through merges and History changes, with fewer canvas flashes.',
    'Paint accurate one-pixel dots and continuous lines, with hard Pencil strokes and hardness-controlled Brush antialiasing.',
    'Keep zoomed pixels sharp across colour depths and inactive documents, and improve document switching.',
    'Correct Eyedropper alignment and Clone Stamp colours and Current & Below sampling after colour conversion.',
    'Improve text bounds, baseline placement, font selection and text selection, plus font-browser docking and reopening.',
    'Refresh mask feather adjustments reliably and show the Space-pan hand cursor promptly.'
  ]},
  {version: 'v0.1.19', build: 'd9b9be8d', date: '1 Nov 2026', added: [], fixes: [
    'Fix missing sections of imported or edited layers when opening documents or converting to ACEScg half/full float.',
    'Preserve complete native layer coverage alongside paint edits, including affected harbour documents, through conversion, Undo/Redo and save/reopen.'
  ]},
  {
    "version": "v0.1.18",
    "build": "bb714a35",
    "date": "1 Nov 2026",
    "added": [
        "Crop with the toolbar: draw and resize a crop, extend beyond the image to grow the canvas, and snap to document bounds, guides or grid.",
        "Drag across layer eyes or Shift-click an endpoint to set visibility for a range of layers.",
        "Auto AI starts off and remembers your preference across launches. Scene Splitter refinement and matte-level sliders support Undo/Redo.",
        "Selecting Brush, Pencil, Eraser, Clone or Blend opens its brush presets while retaining Brush Settings preferences."
    ],
    "fixes": [
        "Restore layer opacity through Undo, Redo and History jumps.",
        "Match ACEScg/linear AI variation thumbnail colours and display clipped generated patches correctly.",
        "Keep complete layers, canvas geometry and RGB thumbnails stable through opening, colour conversion and History navigation.",
        "Reduce large Airbrush mask-painting latency while preserving pixels, pressure, stroke spacing and Undo/Redo.",
        "Protect hidden layers from accidental painting, erasing and adjustments.",
        "Improve layered crop speed and fix float-document crop allocation failures.",
        "Remove horizontal mask-view seams and temporary softening during held brush strokes.",
        "Correct copied-layer RGB thumbnail transparency and match ACEScg half/full-float AI reference previews to the document display.",
        "Block accidental stylus tip-hold workspace menus while preserving deliberate right-click. Use consistent Generate button captions."
    ]
},
  {
    version: 'v0.1.17',
    build: '69514bce',
    date: '31 Oct 2026',
    added: [
      'Scene Layers is now Scene Splitter, with a larger workspace and a collapsible Enhance detail panel.',
      'Add, delete, rename and reorder scene layers. Use visibility eyes to choose preview and export layers, with optional grouped export.',
      'Paint or draw Add/Subtract selections live. Use B, M and L for Brush, Rectangle and Lasso, X to switch Add/Subtract, and Shift-click for straight brush lines.',
      'Adjust Depth guides live, with one Undo per adjustment and individual default resets, while retaining painted areas and enhanced layers.',
      'Keep each generated result on its own transparent Enhanced detail layer, with feathered selection boundaries for overlapping generations.',
    ],
    fixes: [
      'Improve generated-detail alignment and colour matching, and match float-image previews to canvas colour.',
      'Speed up colour-coded preview switching, layer visibility and mask painting after Levels adjustments; keep preview zoom anchored under the cursor.',
      'Simplify mask controls, compact layer names and preview/background buttons.',
      'Synchronize clipped-layer visibility with its base. Open layer effects by double-clicking the layer body; RGB thumbnail clicks no longer open them.',
    ],
  },
  {
    version: 'v0.1.16',
    build: '2ea0bbb1',
    date: '30 Oct 2026',
    added: [
      'Refine individual Scene Layers with rectangle or lasso detail regions, prompt controls and separate transparent restored-detail layers.',
      'Clean up Scene Layers edges with expand/contract, feather and matte levels, rebuild larger regions, and work in linear or ACEScg half/full float.',
      'Apply top-menu blur filters directly to RGB pixels or masks with live canvas previews; layer-stack blur filters remain editable.',
    ],
    fixes: [
      'Improve zoomed-out mask painting, mask Levels previews and mask-preserving sparse Merge Down.',
      'Scroll the layer stack while dragging, restore clipped-layer and group visibility, and show loaded selection outlines.',
      'Keep zoom stable across display modes, commit transforms reliably, reorient flipped handles and support Alt-drag duplication with its cursor.',
      'Improve Shift-click brush lines, tool-specific Alt sampling and Space panning around dialogs; group brush controls to free layer-panel space.',
      'Correct clipped GenAI reference colour and brighter merged or flattened thumbnails.',
      'Keep WSL saves visible in the file chooser and fit long filenames and buttons inside Save Changes dialogs.',
    ],
  },
  {
    version: 'v0.1.15',
    build: 'f1c38f07',
    date: '28 Oct 2026',
    added: [
      'Paint with Watercolour, Oil, Acrylic, Pastel, Coloured Pencil and Ink, with continuous strokes and dedicated media controls.',
      'Choose paper textures and natural or custom colours for Background and Solid Fill layers, with matching thumbnails. Double-click either layer to open the movable Canvas dialog.',
      'Use the Natural media painting MCP skill and sub-skills for brush-only concept art and traditional styles, with visible tool cursors and recording pacing. Connection setup lives in Preferences.',
    ],
    fixes: [
      'Reduce large 16-bit canvas and accumulated Watercolour painting lag while retaining spreading, and improve stroke consistency on Background and transparent layers.',
      'Restore handmade and recycled fibre textures and improve textured-canvas rendering and recovery.',
      'Apply the closest visible canvas or fill surface below each paint layer, including a plain surface with no texture.',
    ],
  },
  {
    version: 'v0.1.14',
    build: '454da105',
    date: '23 Oct 2026',
    fixes: [
      'Restore pen pressure on Linux, tested with a physical tablet on Ubuntu.',
      'Keep layers visible after cropping saved NEE documents.',
      'Improve Square brush edges, mask strokes and Undo.',
      'Fix pen Shift-tap straight lines.',
      'Drag and resize document windows reliably with a pen.',
      'Drag tool dialogs by their headers with a pen.',
    ],
  },
  {
    version: 'v0.1.13',
    build: 'f1bef030',
    date: '22 Oct 2026',
    added: [
      'Choose a black or white background when flattening transparent areas; layer merges retain transparency.',
      'Use optional linear-light mixing where brush strokes overlap on the same 8/16-bit integer sRGB layer.',
    ],
    fixes: [
      'Merge layers faster and keep the canvas stable while merging.',
      'Paint with more consistent pressure, cursor footprint and sampled colours, with restored brush smoothing.',
      'Start float strokes faster and improve WSL painting. Shift-click draws the connected line on press; earlier strokes stay in place.',
      'Improve Leonardo generation response handling and supported GPT Image 2 request dimensions.',
    ],
  },
  {
    version: 'v0.1.12',
    build: 'c6c0d22b',
    date: '22 Oct 2026',
    added: [
      'Drag folders between Image Browser folders with a visible ghost, and see sharper NEE previews.',
      'Use experimental FLUX 3 Klein for Extend with a compatible local AI runtime and companion models.',
    ],
    fixes: [
      'Open large layered files faster and reach sharp detail sooner while zooming and panning.',
      'Fix large linear-to-ACEScg conversion crashes and improve ACEScg / sRGB colour conversion.',
      'Transform selected RGB pixels or mask areas, with reliable Grid Warp commits and previews during zoom.',
      'Improve Green / Blue Screen Merge Down and composite despill sampling, and preserve hidden-layer order during Merge Visible.',
      'Fix large PSD / PSB exports and keep Save As beside the active document.',
      'Improve AI Extend guides, partial-generation error reporting and document closing.',
    ],
  },
  {
    version: 'v0.1.11',
    build: 'dc6c5353',
    date: '20 Oct 2026',
    added: [
      'Open multiple images from the Image Browser with Shift or Ctrl selection.',
      'Convert ACEScg 16/32-bit float images to linear-sRGB while preserving displayed colour.',
      'Report bugs and suggest features from Help, with dedicated Ink support forms.',
    ],
    fixes: [
      'Keep the current Save As filename and folder, and preserve the chosen background in JPEG saves.',
      'Improve image resize correctness and speed, link percentage units and delay progress for quick operations.',
      'Preserve Colour Range matte detail through mask transfers and Apply, with faster sliders and modal opening.',
      'Keep mask views and adjustment-dialog order, and resize Properties by dragging its divider.',
      'Fit tiled documents within the workspace and improve selection visibility, cursors and document closing.',
    ],
  },
  {
    version: 'v0.1.10',
    build: '9a01b4b',
    date: '19 Oct 2026',
    added: [
      'Combine AI screen selection and colour keying, with include/exclude points and object remove/protect lassos.',
      'Refine mattes with finer edge detail, object cleanup, Expand / Contract, Edge Feather and Matte Levels.',
      'Adjust colour-filter sliders and despill colours live, with individual History states for samples, sliders and lassos.',
    ],
    fixes: [
      'Keep applied masks when deleting filters, and preserve the canvas through layer/group reordering and Undo.',
      'Keep Colour Range live after ACEScg conversion, retain plus/minus pickers and preserve conversion colours.',
      'Make live Colour Range and Green / Blue Screen sliders substantially faster on Linux/WebKit.',
      'Start Save As in the active file folder and format, and leave browser sequence grouping off by default.',
      'Correct PNG duplicate rendering and closing/reopening image document windows.',
    ],
  },
  {
    version: 'v0.1.9',
    build: '89f92c4',
    date: '18 Oct 2026',
    added: [
      'Switch Image Browser file filters and views, resize thumbnails and toggle the preview.',
      'Pin folders to Quick access and browse compact tiles with clearer drive, network and ZIP icons.',
      'Extract ZIP archives from the browser right-click menu.',
      'Maximise the Image Browser and drag its header to return to windowed view.',
    ],
    fixes: [
      'Move the Image Browser smoothly and keep the app visible behind modal windows.',
      'Start standard Open with All files and a shorter supported-files label.',
      'Fix JPEG AI Remove failing with a false black-crop error and clear stale ready text after generation errors.',
    ],
  },
  {
    version: 'v0.1.8',
    build: 'c03ce71',
    date: '18 Oct 2026',
    added: [
      'Adjust surrounding context and AI crop resolution in Qwen Extend.',
      'Undo and redo selections and Deselect through History.',
    ],
    fixes: [
      'Improve Qwen Edit and Turbo Extend alignment, edge blending and colour matching.',
      'Keep the chosen AI mode when making a new selection.',
      'Improve local model switching and ComfyUI startup reliability.',
      'Press Enter to confirm supported dialogs and adjustments.',
      'Correct WSL display scaling, file dialog size and cursor size.',
    ],
  },
  {
    version: 'v0.1.7',
    build: '2d4ef49',
    date: '17 Oct 2026',
    added: [
      'Extract ZIP archives from File Open and the Image Browser.',
      'Warm the selected local AI model and workflow in the background.',
      'Restore a maximised document window by dragging its header.',
    ],
    fixes: [
      'Open layered PSB, PSD, EXR and NEE files faster, with a sharp composite appearing together.',
      'Flatten large native EXRs faster, including immediately after opening.',
      'Keep cropped bounds and centred canvas enlargement correct through flattening, saving and reopening.',
      'Save large flattened TIFFs beyond 2 GB, with depth, compression and transparency options.',
      'Correct copied layer masks, mask dragging, Shift-constrained moves and Undo thumbnail selection.',
      'Keep Layer Effects on their own layer and preserve thumbnail colours and transparency.',
    ],
  },
  {
    version: 'v0.1.6',
    build: '6ec654f',
    date: '15 Oct 2026',
    added: [
      'Adjust surrounding context and AI crop resolution in Fill, with the same controls as Remove.',
      'Convert Linear / ACEScg images to sRGB in either 8-int or 16bit-int.',
    ],
    fixes: [
      'Keep layer thumbnail colours correct after Linear / ACEScg to sRGB conversion in both 8-bit and 16-bit integer documents.',
      'Use Layer Effects on large 7k and 18k images, with effects shown across the whole layer thumbnail.',
      'Close and reopen documents reliably without hidden open documents or layers appearing in another document.',
      'Preserve colour through float Linear sRGB to ACEScg conversion and enable AI Fill in linear float documents.',
      'Apply clipped Hue/Saturation adjustments correctly to AI layers, including at reduced layer opacity.',
      'Use smaller Auto crops for tiny Qwen Remove selections and avoid crop-control crashes and streaked later variations.',
      'Give Qwen Turbo Remove a clearer object description and selection location.',
      'Show the AI context preview and prepare the source correctly after conversion to 16-bit integer sRGB.',
    ],
  },
  {
    version: 'v0.1.5',
    build: '7ec7801',
    date: '15 Oct 2026',
    fixes: [
      'Open large layered EXRs faster and reach full-resolution detail sooner when zooming in.',
      'Merge supported masked layers, clipped adjustments and nested groups faster while preserving native bit depth.',
      'Keep full-resolution detail after merges, with consistent fractional-zoom display through panning and Undo/Redo.',
      'Speed up Linear sRGB to ACEScg conversion and preserve colours, transparency and thumbnails in half- and full-float workflows.',
      'Improve Levels responsiveness and Alpha Levels merging on large float documents.',
      'Offer Merge Selected when an expanded group and its children are selected, alongside Merge Group.',
      'Restore layer and channel thumbnails more reliably through merge and history changes.',
    ],
  },
  {
    "version": "v0.1.4",
    "build": "4db0ebf",
    "date": "13 Oct 2026",
    "added": [
        "Create brush tips from images or canvas selections; organise, import and export brush preset groups.",
        "Rename saved brush presets from their right-click menu and resize the brush library lists with draggable dividers.",
        "Use sharp perspective guide brushes and a thinner Vertical Rectangle tip, with brushes up to 5000px.",
        "See document-loading feedback beside the tabs and a movable Neevii update notice with the correct Windows or Linux download."
    ],
    "fixes": [
        "Merge supported large float EXR layers much faster after opening; the tested Merge Down pair completes in about half a second.",
        "Keep fast merges free of a flashing progress bar and hide background refinement feedback.",
        "Save supported native NEE documents with lossless compression while preserving layers and full-float pixel data.",
        "Improve native float brush opacity, geometric tip behaviour and brush rotation when switching tips.",
        "Reduce Linux float-document display and zoom stalls. Brief loading pauses can still occur."
    ]
},
  {
    "version": "v0.1.3",
    "build": "91cf995",
    "date": "11 Oct 2026",
    "fixes": [
        "Keep duplicated Background layers visible after AI Remove operations, including JPEG and other 8-bit images.",
        "Open local AI model controls on Linux without asking to unlock a saved cloud-provider keyring."
    ]
},
  {
    "version": "v0.1.3",
    "build": "b9e538e",
    "date": "11 Oct 2026",
    "added": [
      "Image Browser with thumbnails, file details, previews, named drives and movable, resizable windows.",
      "Cached sequence playback, multiple frames to open, and PNG/TIFF/raster sequences alongside EXR.",
      "New folder, inline Rename, Copy, Paste, Delete and Copy as Path for selected files and folders.",
      "Qwen Image 2.1 Turbo evaluation model for local Fill, Remove, Extend and Enhance Detail.",
      "Context and crop-resolution controls for Qwen Turbo Fill and Remove."
    ],
    "fixes": [
      "Allow Qwen Turbo Fill to add prompted objects to cleared areas, with more reliable selection placement.",
      "Load large 32-bit linear Photoshop layer stacks correctly and preview PDF first pages in the Windows browser.",
      "Match EXR browser and sequence previews to canvas colours, and remove preview block bands.",
      "Enhance HDR detail with float layer output, optional source alignment and colour matching, and fewer colour-correction streaks.",
      "Keep temporary and recovery files in the configured scratch location.",
      "Match Turbo removal patch colour and alignment more consistently.",
      "Preserve duplicated Background pixels and visibility through Merge Down, Undo and History."
    ]
  },
  {
    version: 'v0.1.1',
    build: '4fe582b',
    date: '9 Oct 2026',
    added: [
      'Browse EXR image sequences in a timeline, scrub frames, and open a chosen frame at full quality for editing.',
      'Choose named sequences in the folder browser, or turn off View as image sequence to browse individual images.',
      'Find Check for Updates and the current app version in the Help menu.',
    ],
    fixes: [
      'Pressing Enter now completes transforms on copied float layers with complete pixel data.',
      'Document loading progress now uses the app’s purple accent.',
    ],
  },
  {
    version: 'v0.1.0',
    build: '0fe13a0',
    date: '8 Oct 2026',
    fixes: [
      'Move layers more responsively without reducing preview resolution.',
      'Transform multiple selected layers together while keeping each layer editable.',
      'Gaussian, Surface and Directional Blur now soften pixel-layer colour and transparency correctly.',
      'Keep the canvas aligned and restore visibility correctly when toggling clipped blur.',
    ],
  },
  {
    version: 'v0.1.0',
    build: '833408d',
    date: '8 Oct 2026',
    fixes: ['Free Transform corner adjustments on 16- and 32-bit float layers now apply correctly when you press Enter.'],
  },
  {
    version: 'v0.1.0',
    build: 'e7c86f4',
    date: '7 Oct 2026',
    added: [
      'Remove: crop and context controls, with a live preview of the AI reference area',
      'Remove: optional object instructions for local and cloud models',
      'Lasso-aware AI reference context',
      'Clone Stamp can sample the current layer and the layers below',
      'Canvas Size keeps width and height linked and shows the resulting pixels',
      'Manage installed local AI models in Preferences: enable, disable or uninstall',
    ],
    fixes: [
      'HDR patches merge and export without revealing transparent pixels',
      'Selections and Flatten work correctly after resizing the canvas',
      'Fixed-ratio marquee selections stay constrained at the image edges',
      'Copied pixels survive undo and redo',
    ],
  },
  {
    version: 'v0.1.0',
    build: '2ab07e3',
    date: '25 Sep 2026',
    added: ['First public build, with offline installers for Windows and Linux.'],
  },
];

/* Dark canvas behind the page while an Ink route is mounted. */
export function useInkRoot() {
  useEffect(() => {
    document.documentElement.classList.add('ink2-root');
    return () => document.documentElement.classList.remove('ink2-root');
  }, []);
}

/* Soft cursor light (desktop pointers only). */
export function Glow() {
  const [pos, setPos] = useState({ x: -600, y: -600 });
  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return undefined;
    const move = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('pointermove', move);
    return () => window.removeEventListener('pointermove', move);
  }, []);
  return <div className="glow" style={{ left: pos.x, top: pos.y }} aria-hidden="true" />;
}

export function InkNav({ onHome }) {
  const sec = (id) => (onHome ? `#${id}` : `/ink#${id}`);
  return (
    <nav className="nav" aria-label="Neevii Ink">
      <Link className="brand" to="/ink">
        <img src={LOGO_256} alt="" width="34" height="34" />
        <span>
          Neevii <span className="inkword">Ink</span>
        </span>
      </Link>
      <a className="l" href={sec('colour')}>Colour</a>
      <a className="l" href={sec('generative')}>Generative</a>
      <a className="l" href={sec('local-ai')}>Local AI</a>
      <Link className="l" to="/get-ink">Licence</Link>
      <InkContactMenu />
      <Link className="btn btn-p" to="/get-ink#download">↓ Download</Link>
    </nav>
  );
}

export function InkFooter() {
  return (
    <footer className="foot">
      <div className="wrap">
        <span>© {new Date().getFullYear()} Neevii · Neevii Ink is built by a working VFX artist.</span>
        <nav aria-label="Footer">
          <Link to="/">Neevii</Link>
          <Link to="/get-ink">Download</Link>
          <a href="mailto:ink-support@neevii.com">ink-support@neevii.com</a>
          <Link to="/ink/report-bug">Report a bug</Link>
          <Link to="/ink/suggest-feature">Suggest a feature</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </div>
    </footer>
  );
}

/* Click-to-enlarge for screenshots and demo videos. */
export function useLightbox() {
  const [item, setItem] = useState(null);
  useEffect(() => {
    if (!item) return undefined;
    const esc = (e) => e.key === 'Escape' && setItem(null);
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [item]);
  const box = item ? (
    <div className="lb" role="dialog" aria-label="Enlarged view" onClick={() => setItem(null)}>
      {item.type === 'video' ? (
        <video src={item.src} autoPlay muted loop playsInline />
      ) : (
        <img src={item.src} alt={item.alt || ''} />
      )}
    </div>
  ) : null;
  return [setItem, box];
}
